import { TProject, TProjectItem } from '@/types/models.ts';
import { AppDialog } from '@/components/app/app-dialog.tsx';
import { AppForm } from '@/components/form/form.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { d } from '@/lib/form.ts';
import { InputField } from '@/components/form/input-field.tsx';
import { Button } from '@/components/ui/button.tsx';
import { useSaveProjectAreaMutation } from '@/app/account/api/queries.ts';
import { setUiUpdated } from '@/stores/ui.store.ts';

const formSchema = z.object({
  project_id: z.number(),
  item_id: z.number(),
  area: z.string().min(1, 'Area name is required'),
});

export type FormType = z.infer<typeof formSchema>;

type Props = {
  project: TProject;
  item: TProjectItem;
  onClose: () => void;
};

export function AddNewArea({ project, item, onClose }: Props) {
  const saveProjectArea = useSaveProjectAreaMutation();

  const form = useForm<FormType>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      project_id: d.num(project.id),
      item_id: d.num(item.id),
      area: '',
    },
  });

  const onSubmit = async (data: FormType) => {
    await saveProjectArea.mutateAsync(data);
    setUiUpdated();
    onClose();
  }

  return (
    <AppDialog title={`Add new area for project: ${project.name}`} onClose={onClose}>
      <AppForm form={form} onSubmit={onSubmit} className="w-full space-y-4 p-2 mx-auto">
        <div className="dialog-body">
          <InputField name="area" label="Area Name" placeholder="New area name" required autoFocus />
        </div>
        <div className="dialog-footer">
          <Button disabled={!form.formState.isDirty}>Save</Button>
        </div>
      </AppForm>
    </AppDialog>
  );
}
